import React, { useEffect, useState, useMemo } from 'react';
import { Shield, ChevronRight, ChevronDown, Check, X, Search, Eye, Lock, Users } from 'lucide-react';
import { NEU } from '../neuStyles';
import PrintButton from '../components/PrintButton';
import { manageableRoles } from '../lib/roleCatalog';

// Permission categories for grouped display
const PERMISSION_CATEGORIES = {
  'Patient Records': ['patients:read', 'patients:create', 'patients:update', 'patients:delete', 'patients:export'],
  'Encounters': ['encounters:read', 'encounters:create', 'encounters:update', 'encounters:sign'],
  'Medications': ['medications:read', 'medications:create', 'medications:update', 'medications:delete'],
  'Lab Results': ['labs:read', 'labs:create', 'labs:update'],
  'Vitals': ['vitals:read', 'vitals:create', 'vitals:update'],
  'Allergies': ['allergies:read', 'allergies:create', 'allergies:update', 'allergies:delete'],
  'User Management': ['users:read', 'users:create', 'users:update', 'users:delete'],
  'Audit & Compliance': ['audit:read', 'audit:export'],
  'System Settings': ['settings:read', 'settings:update'],
  'Dashboard': ['dashboard:read'],
  'Secure Chat': ['chat:read', 'chat:create', 'chat:create_channel', 'chat:delete', 'chat:delete_channel'],
  'Secure Email': ['email:read', 'email:create', 'email:send_external', 'email:delete', 'email:config'],
  'AI Research': ['ai_research:read', 'ai_research:create', 'research:read', 'research:create'],
  'Business Intelligence': ['bi:read', 'bi:create', 'bi:export'],
  'Billing': ['billing:read', 'billing:create', 'billing:update', 'billing:delete', 'billing:export'],
  'CRM': ['crm:read', 'crm:create', 'crm:update', 'crm:delete', 'crm:export'],
  'Admin AI': ['admin_ai:read', 'admin_ai:create'],
  'Scheduling': ['scheduling:read', 'scheduling:create', 'scheduling:update', 'scheduling:delete'],
  'Notifications': ['notifications:read', 'notifications:create'],
  'Data Export': ['export:patients', 'export:billing', 'export:audit', 'export:clinical'],
  'Two-Factor Auth': ['totp:setup'],
  'Patient Portal': ['portal:manage'],
  'Reports': ['reports:read', 'reports:create', 'reports:export'],
  'AI Alerts': ['alerts:read', 'alerts:create', 'alerts:update'],
  'Telemedicine': ['telemed:read', 'telemed:create', 'telemed:update'],
  'Insurance': ['insurance:read', 'insurance:create', 'insurance:update'],
  'Personnel': ['personnel:read', 'personnel:create', 'personnel:update', 'personnel:delete'],
  'Leave Requests': ['leave:read', 'leave:create', 'leave:update', 'leave:approve'],
  'Biolab Documents': ['biolab_docs:read', 'biolab_docs:upload', 'biolab_docs:analyze', 'biolab_docs:delete'],
  'Clinical AI': ['clinical_ai:read', 'clinical_ai:create', 'clinical_ai:quick'],
  'CRM AI': ['crm_ai:read', 'crm_ai:create', 'crm_ai:quick'],
  'Billing AI': ['billing_ai:read', 'billing_ai:create', 'billing_ai:quick'],
  'Scheduling AI': ['scheduling_ai:read', 'scheduling_ai:create', 'scheduling_ai:quick'],
};

// DERIVED from src/lib/roleCatalog.js — this file used to declare its own
// 12-entry array, which is why `sales` (10 accounts) and `patient_experience`
// (60 permissions) were absent from the permission matrix entirely. Add roles to
// the catalog; roleCatalogConformance.test.js asserts nothing is left unreachable.
// The "ten live users" this comment used to claim is no longer true: measured in
// prod 2026-08-06, sales holds 10 rows but only ONE is is_active (ana.ituarte).
const ALL_ROLES = manageableRoles();

export default function RoleManagementPage({ user }) {
  const [selectedRole, setSelectedRole] = useState('admin');
  const [rolePermissions, setRolePermissions] = useState({});
  const [allPermissions, setAllPermissions] = useState({});
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [searchText, setSearchText] = useState('');
  const [compareRole, setCompareRole] = useState(null);
  const [comparePermissions, setComparePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userCounts, setUserCounts] = useState({});

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [permsResult, usersResult] = await Promise.all([
          window.zignagenixAPI.getRBACData(),
          window.zignagenixAPI.getUsers(),
        ]);
        if (permsResult && !permsResult.error) {
          setRolePermissions(permsResult.rolePermissions || {});
          setAllPermissions(permsResult.permissions || {});
        }
        const userList = Array.isArray(usersResult) ? usersResult : (usersResult?.data || []);
        const counts = {};
        userList.forEach(u => { counts[u.role] = (counts[u.role] || 0) + 1; });
        setUserCounts(counts);
      } catch (err) {
        console.error('Error loading RBAC data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (compareRole && rolePermissions[compareRole]) {
      setComparePermissions(rolePermissions[compareRole]);
    } else {
      setComparePermissions([]);
    }
  }, [compareRole, rolePermissions]);

  const currentPerms = useMemo(() => rolePermissions[selectedRole] || [], [selectedRole, rolePermissions]);

  // Roles A→Z by display label — DISPLAY ORDER ONLY (role values/permissions
  // untouched; `find`/count still use ALL_ROLES). Used for the list + compare picker.
  const sortedRoles = useMemo(() =>
    [...ALL_ROLES].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })), []);

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const expandAll = () => setExpandedCategories(new Set(Object.keys(PERMISSION_CATEGORIES)));
  const collapseAll = () => setExpandedCategories(new Set());

  const totalPermissions = Object.keys(allPermissions).length || 109;
  const rolePermCount = currentPerms.length;

  const filteredCategories = useMemo(() => {
    // #233 follow-up: sort the category groups A→Z by their displayed label (in
    // this tab the object KEY is the display label), locale-aware/case-insensitive.
    // Display order only — membership/keys unchanged (Expand/Collapse-all, compare
    // diffing, counts, and search all keep working over the sorted view).
    const byLabel = ([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' });
    if (!searchText.trim()) return Object.entries(PERMISSION_CATEGORIES).sort(byLabel);
    const s = searchText.toLowerCase();
    return Object.entries(PERMISSION_CATEGORIES)
      .map(([cat, perms]) => {
        const filtered = perms.filter(p =>
          p.toLowerCase().includes(s) ||
          (allPermissions[p] || '').toLowerCase().includes(s) ||
          cat.toLowerCase().includes(s)
        );
        return filtered.length > 0 ? [cat, filtered] : null;
      })
      .filter(Boolean)
      .sort(byLabel);
  }, [searchText, allPermissions]);

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <p style={{ color: '#6B7280' }}>Loading RBAC configuration...</p>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ ...NEU.statIcon, background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff' }}>
            <Shield size={22} />
          </div>
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>Role & Permission Management</h1>
            <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>
              {ALL_ROLES.length} roles &bull; {totalPermissions} permissions &bull; HIPAA minimum-necessary access
            </p>
          </div>
        </div>
        <PrintButton pageTitle="Role & Permission Management" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24 }}>
        {/* Role List Sidebar */}
        <div style={{ ...NEU.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(186,199,216,0.3)' }}>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Roles
            </h3>
          </div>
          {sortedRoles.map(role => {
            const isSelected = selectedRole === role.value;
            const permCount = (rolePermissions[role.value] || []).length;
            const uCount = userCounts[role.value] || 0;
            return (
              <button key={role.value} onClick={() => setSelectedRole(role.value)}
                style={{
                  display: 'block', width: '100%', padding: '12px 16px', border: 'none',
                  textAlign: 'left', cursor: 'pointer', transition: 'all 0.15s',
                  background: isSelected ? `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})` : NEU.bg,
                  color: isSelected ? '#fff' : '#1f2937',
                  borderBottom: '1px solid rgba(186,199,216,0.2)',
                }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{role.label}</div>
                <div style={{ fontSize: 11, opacity: 0.8, display: 'flex', gap: 8 }}>
                  <span>{permCount} perms</span>
                  <span>&bull;</span>
                  <span>{uCount} user{uCount !== 1 ? 's' : ''}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Permission Detail Panel */}
        <div>
          {/* Role Summary Card */}
          <div style={{ ...NEU.card, padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: '#1f2937' }}>
                  {ALL_ROLES.find(r => r.value === selectedRole)?.label}
                </h2>
                <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                  {ALL_ROLES.find(r => r.value === selectedRole)?.description}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: NEU.teal }}>{rolePermCount}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>of {totalPermissions} permissions</div>
                {/* Progress bar */}
                <div style={{ width: 120, height: 6, background: '#e5e7eb', borderRadius: 3, marginTop: 6 }}>
                  <div style={{
                    width: `${(rolePermCount / totalPermissions) * 100}%`,
                    height: '100%', borderRadius: 3,
                    background: `linear-gradient(90deg, ${NEU.teal}, ${NEU.tealLight})`,
                  }} />
                </div>
              </div>
            </div>
          </div>

          {/* Compare Role Selector */}
          <div style={{ ...NEU.card, padding: 16, marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <Search size={16} style={{ color: '#9ca3af' }} />
                <input type="text" placeholder="Search permissions..."
                  value={searchText} onChange={e => setSearchText(e.target.value)}
                  style={{ ...NEU.input, flex: 1, padding: '8px 12px', fontSize: 13 }} />
              </div>
              <select value={compareRole || ''} onChange={e => setCompareRole(e.target.value || null)}
                style={{ ...NEU.input, padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}>
                <option value="">Compare with...</option>
                {sortedRoles.filter(r => r.value !== selectedRole).map(r =>
                  <option key={r.value} value={r.value}>{r.label}</option>
                )}
              </select>
              <button className="btn-ghost btn-sm" onClick={expandAll}
                style={{ padding: '8px 12px', fontSize: 12 }}>Expand All</button>
              <button className="btn-ghost btn-sm" onClick={collapseAll}
                style={{ padding: '8px 12px', fontSize: 12 }}>Collapse All</button>
            </div>
          </div>

          {/* Permission Categories */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredCategories.map(([category, perms]) => {
              const isExpanded = expandedCategories.has(category);
              const grantedCount = perms.filter(p => currentPerms.includes(p)).length;
              const allGranted = grantedCount === perms.length;
              const noneGranted = grantedCount === 0;

              return (
                <div key={category} style={{ ...NEU.cardSm, overflow: 'hidden' }}>
                  <button onClick={() => toggleCategory(category)}
                    style={{
                      display: 'flex', width: '100%', alignItems: 'center', gap: 10,
                      padding: '12px 16px', border: 'none', background: 'transparent',
                      cursor: 'pointer', textAlign: 'left',
                    }}>
                    {isExpanded ? <ChevronDown size={16} color="#6b7280" /> : <ChevronRight size={16} color="#6b7280" />}
                    <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: '#1f2937' }}>{category}</span>
                    <span style={{
                      ...NEU.badge, padding: '2px 10px',
                      background: allGranted ? '#dcfce7' : noneGranted ? '#fef2f2' : '#fefce8',
                      color: allGranted ? '#166534' : noneGranted ? '#991b1b' : '#854d0e',
                    }}>
                      {grantedCount}/{perms.length}
                    </span>
                  </button>
                  {isExpanded && (
                    <div style={{ padding: '0 16px 12px' }}>
                      {/* #233 follow-up: permission rows A→Z by key (display order only). */}
                      {[...perms].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map(perm => {
                        const hasIt = currentPerms.includes(perm);
                        const compareHasIt = compareRole ? comparePermissions.includes(perm) : null;
                        return (
                          <div key={perm} style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
                            borderTop: '1px solid rgba(186,199,216,0.2)',
                          }}>
                            {hasIt ? (
                              <Check size={16} color="#16a34a" />
                            ) : (
                              <Lock size={16} color="#d1d5db" />
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: hasIt ? '#1f2937' : '#9ca3af',
                                fontFamily: 'monospace' }}>{perm}</div>
                              <div style={{ fontSize: 11, color: '#6b7280' }}>{allPermissions[perm] || ''}</div>
                            </div>
                            {compareRole !== null && (
                              <span style={{
                                fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 600,
                                background: compareHasIt ? '#dcfce7' : '#fef2f2',
                                color: compareHasIt ? '#166534' : '#991b1b',
                              }}>
                                {compareHasIt ? 'Yes' : 'No'}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {filteredCategories.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              No permissions match your search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
